/**
 * Cookie consent — typy, defaulty i helpery localStorage.
 * Zgodne z RODO (art. 6 ust. 1 lit. a) i dyrektywą ePrivacy.
 */

export type ConsentCategoryKey = 'necessary' | 'functional' | 'analytics' | 'marketing';

export type ConsentCategories = {
  necessary: true; // zawsze włączone — niezbędne technicznie
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type StoredConsent = {
  version: 1;
  timestamp: string; // ISO 8601 — kiedy udzielono / ostatnio zmieniono zgodę
  categories: ConsentCategories;
};

export const STORAGE_KEY = 'kuznia-cookie-consent-v1';
export const CONSENT_EVENT = 'kuznia:consent-changed';
export const CONSENT_VALIDITY_MS = 365 * 24 * 60 * 60 * 1000; // 12 miesięcy

export const DEFAULT_CATEGORIES: ConsentCategories = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export const ALL_ACCEPTED: ConsentCategories = {
  necessary: true,
  functional: true,
  analytics: true,
  marketing: true,
};

export const CATEGORY_META: Record<
  ConsentCategoryKey,
  { label: string; description: string; required?: boolean }
> = {
  necessary: {
    label: 'Niezbędne',
    description:
      'Konieczne do prawidłowego działania strony — zapamiętanie Twoich wyborów dotyczących cookies oraz sesja logowania administratora. Tych plików nie można wyłączyć.',
    required: true,
  },
  functional: {
    label: 'Funkcjonalne',
    description:
      'Pozwalają zapamiętać Twoje preferencje (np. zamknięcie paska informacyjnego), aby wygodniej korzystać ze strony.',
  },
  analytics: {
    label: 'Analityczne',
    description:
      'Pomagają nam zrozumieć, jak korzystasz ze strony — w sposób anonimowy i zbiorczy. Dzięki nim możemy ulepszać treści.',
  },
  marketing: {
    label: 'Marketingowe',
    description:
      'Wykorzystywane do wyświetlania treści dopasowanych do Twoich zainteresowań w mediach społecznościowych. Aktualnie nieużywane — kategoria zarezerwowana na przyszłość.',
  },
};

export function loadConsent(): StoredConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== 1 || !parsed.timestamp || !parsed.categories) return null;
    // wygasła zgoda — wymuszamy ponowne pytanie
    const ts = new Date(parsed.timestamp).getTime();
    if (Number.isNaN(ts) || Date.now() - ts > CONSENT_VALIDITY_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(categories: ConsentCategories): StoredConsent {
  const stored: StoredConsent = {
    version: 1,
    timestamp: new Date().toISOString(),
    categories: { ...categories, necessary: true },
  };
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: stored }));
  }
  return stored;
}

export function clearConsent() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
}

/** Globalny event do otwierania dialogu z dowolnego miejsca. */
export const OPEN_SETTINGS_EVENT = 'kuznia:open-cookie-settings';

export function openCookieSettings() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT));
}
